import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Employee Management related images (keep your existing imports or replace with EMS images)
import payrollImg from "../assets/images/payroll.jpg";
import attendanceImg from "../assets/images/attendance.jpg";
import performanceImg from "../assets/images/perfomance.jpg";
// recruitment.jpg not present in the assets folder; use a present image as fallback
import recruitmentImg from "../assets/images/team.webp";
import hrAnalyticsImg from "../assets/images/eco.webp";
import leaveManagementImg from "../assets/images/leave.jpg";
import trainingImg from "../assets/images/training.jpg";
import hrComplianceImg from "../assets/images/documentation.jpg";
import workforcePlanningImg from "../assets/images/workforce.jpg";

const blogs = [
    {
    id: 2,
    title: "Automated Payroll Management Best Practices",
    slug: "automated-payroll-management-best-practices",
    image: payrollImg,
    category: "Payroll",
    date: "2024-01-13",
    readTime: "4 min read",
    modalContent: "Automated payroll systems eliminate manual errors, ensure tax compliance, and save countless hours of HR processing time. Discover best practices for implementing payroll automation, including integration with time tracking, benefits administration, and real-time reporting capabilities.",
  },
  {
    id: 3,
    title: "Modern Attendance Tracking Systems",
    slug: "modern-attendance-tracking-systems",
    image: attendanceImg,
    category: "Attendance",
    date: "2024-01-12",
    readTime: "6 min read",
    modalContent: "Gone are the days of punch cards and manual timesheets. Modern attendance systems leverage biometrics, GPS, and mobile apps to provide accurate, real-time tracking. Explore how AI-powered attendance solutions reduce time theft and provide valuable workforce insights.",
  },
  {
    id: 4,
    title: "Performance Management in Remote Teams",
    slug: "performance-management-remote-teams",
    image: performanceImg,
    category: "Performance",
    date: "2024-01-11",
    readTime: "7 min read",
    modalContent: "Managing performance remotely requires new approaches and tools. Learn how continuous feedback loops, OKR tracking, and regular check-ins can keep remote teams aligned and motivated. Discover software solutions that make remote performance management seamless and effective.",
  },
  {
    id: 5,
    title: "AI-Powered Recruitment Strategies",
    slug: "ai-powered-recruitment-strategies",
    image: recruitmentImg,
    category: "Recruitment",
    date: "2024-01-10",
    readTime: "5 min read",
    modalContent: "Artificial intelligence is revolutionizing talent acquisition. From resume screening to candidate matching, AI tools help HR teams find the right talent faster. Learn how to implement ethical AI in your recruitment process while reducing bias and improving quality of hire.",
  },
  {
    id: 6,
    title: "HR Analytics for Data-Driven Decisions",
    slug: "hr-analytics-data-driven-decisions",
    image: hrAnalyticsImg,
    category: "HR Analytics",
    date: "2024-01-09",
    readTime: "8 min read",
    modalContent: "Data-driven HR is the future of workforce management. Learn how to leverage people analytics to reduce turnover, improve engagement, and predict hiring needs. Explore key metrics every HR leader should track and the tools that make analysis accessible.",
  },
  {
    id: 8,
    title: "Leave Management Automation",
    slug: "leave-management-automation",
    image: leaveManagementImg,
    category: "Leave Management",
    date: "2024-01-07",
    readTime: "4 min read",
    modalContent: "Manual leave tracking leads to confusion and errors. Automate your leave management with systems that handle requests, approvals, and balances seamlessly. Learn about policy configuration, accrual tracking, and integration with payroll for accurate PTO calculations.",
  },
  {
    id: 9,
    title: "Employee Training & Development Platforms",
    slug: "employee-training-development-platforms",
    image: trainingImg,
    category: "Training",
    date: "2024-01-06",
    readTime: "5 min read",
    modalContent: "Continuous learning is key to employee satisfaction and retention. Explore modern LMS platforms that offer personalized learning paths, skill gap analysis, and certification tracking. Learn how to build a culture of learning that drives business results.",
  },
  {
    id: 10,
    title: "HR Compliance & Document Management",
    slug: "hr-compliance-document-management",
    image: hrComplianceImg,
    category: "Compliance",
    date: "2024-01-05",
    readTime: "6 min read",
    modalContent: "Staying compliant with labor laws is critical. Learn how digital document management systems help HR teams maintain secure, organized, and auditable employee records. Discover features like automated reminders for document expiry and role-based access controls.",
  },
  {
    id: 11,
    title: "Strategic Workforce Planning",
    slug: "strategic-workforce-planning",
    image: workforcePlanningImg,
    category: "Workforce Planning",
    date: "2024-01-04",
    readTime: "7 min read",
    modalContent: "Strategic workforce planning ensures you have the right people in the right roles. Learn how to forecast future talent needs, identify skill gaps, and create succession plans. Discover tools that help visualize workforce data and simulate different scenarios.",
  },
];

const BlogCarousel = () => {
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (blog) => {
    setSelectedBlog(blog);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBlog(null);
    document.body.style.overflow = "unset";
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleEscKey);
    return () => window.removeEventListener("keydown", handleEscKey);
  }, []);

  return (
    <section className="py-12 bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
            <span className="text-sm font-medium tracking-wide">Latest Articles</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Employee Management <span className="text-blue-600">Insights</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Expert articles, best practices, and trends in HR technology and workforce management
          </p>
        </div>

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={24}
          loop={true}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false
          }}
          pagination={{ 
            clickable: true,
            dynamicBullets: true
          }}
          navigation={{
            enabled: true
          }}
          breakpoints={{
            320: {
              slidesPerView: 1,
              navigation: false
            },
            480: {
              slidesPerView: 1,
              navigation: false
            },
            640: {
              slidesPerView: 1,
              navigation: false
            },
            768: {
              slidesPerView: 2,
              navigation: false
            },
            1024: {
              slidesPerView: 3,
              navigation: true
            }
          }}
        >
          {blogs.map((blog) => (
            <SwiperSlide key={blog.id}>
              <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 h-full flex flex-col">
                {/* Image Container */}
                <div className="relative overflow-hidden h-48">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg">
                      {blog.category}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                
                {/* Content */}
                <div className="p-5 flex flex-col flex-grow">
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {blog.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {blog.readTime}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {blog.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {blog.modalContent.substring(0, 100)}...
                  </p>
                  
                  <button
                    onClick={() => openModal(blog)}
                    className="mt-auto flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white px-4 py-2 rounded-xl transition-all duration-300 text-sm font-medium"
                  >
                    Read Article
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Modal */}
      {isModalOpen && selectedBlog && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fadeIn"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedBlog.image}
                alt={selectedBlog.title}
                className="w-full h-64 object-cover rounded-t-2xl"
              />
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  {selectedBlog.category}
                </span>
                <span className="text-xs text-gray-500">{selectedBlog.date}</span>
                <span className="text-xs text-gray-500">{selectedBlog.readTime}</span>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedBlog.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                {selectedBlog.modalContent}
              </p>
              
              <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                <div className="flex gap-2">
                  <button className="text-gray-400 hover:text-blue-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  <button className="text-gray-400 hover:text-blue-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={closeModal}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default BlogCarousel;